import React from 'react';
import { Box, Stack, Typography, Tooltip, IconButton } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useTranslation } from 'react-i18next';
import PaiButton from 'features/ui/base-components/pai-button';

interface Props {
  funnel: any;
  isMostPopular: boolean;
  isBestConversion: boolean;
  likeLoading: boolean;
  /** Rendered next to the language badge, so admin controls no longer overlap the title. */
  adminActions?: React.ReactNode;
  onOpenDetails(funnel: any): void;
  onLike(funnelId: string): void;
  onApply(funnel: any): void;
}

/**
 * The list endpoint returns the persisted `likeCount`, while the like endpoint answers
 * with `likes`. Reading only one of them made the counter reappear on click and vanish
 * again on reload. Accept both rather than pick a side.
 */
const readLikes = (funnel: any): number => Number(funnel?.likes ?? funnel?.likeCount ?? 0);

/**
 * Same suffix heuristic the page uses for its language filter — there is no language field
 * on the model, only the "(DE)" / "(EN)" suffix every template carries. Templates without a
 * recognised suffix simply show no badge instead of a wrong one.
 */
const languageBadgeOf = (funnel: any): string | null => {
  const name = String(funnel?.name ?? '');
  if (/\(DE\)\s*$/i.test(name)) return 'DE';
  if (/\(EN\)\s*$/i.test(name)) return 'EN';
  return null;
};

const GlobalFunnelCard: React.FC<Props> = ({
  funnel,
  isMostPopular,
  isBestConversion,
  likeLoading,
  adminActions,
  onOpenDetails,
  onLike,
  onApply,
}) => {
  const { t, i18n } = useTranslation();

  const likes = readLikes(funnel);
  const languageBadge = languageBadgeOf(funnel);

  const conversion = Number(funnel?.conversionRate);
  const conversionLabel = Number.isFinite(conversion)
    ? `${conversion.toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
    : '–';
  const usesLabel = String(Number(funnel?.useCount ?? funnel?.uses ?? 0));
  const stepsLabel = String(Array.isArray(funnel?.steps) ? funnel.steps.length : Number(funnel?.steps ?? 0));

  const metrics: { label: string; value: string }[] = [
    { label: t('useCaseStoreMetricConversion'), value: conversionLabel },
    { label: t('useCaseStoreMetricUses'), value: usesLabel },
    { label: t('useCaseStoreMetricSteps'), value: stepsLabel },
  ];

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        p: '20px',
        cursor: 'pointer',
        backgroundColor: '#FFF',
        border: '1px solid #E3E3E8',
        borderRadius: '12px',
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          borderColor: (theme) => theme.palette.primary.main,
          boxShadow: '0 4px 12px 0 rgba(0,0,0,0.12)',
        },
      }}
      onClick={() => onOpenDetails(funnel)}
      tabIndex={0}
      role="button"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpenDetails(funnel);
      }}
    >
      {/* Title row — the language badge and the admin controls share the right edge, so
          neither can end up on top of the name the way the pencil icon used to. */}
      <Stack direction="row" alignItems="flex-start" gap={1}>
        <Typography
          sx={{ flex: 1, fontSize: '17px', fontWeight: 500, lineHeight: 1.3, overflowWrap: 'anywhere' }}
        >
          {funnel.name}
        </Typography>
        {languageBadge && (
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              fontSize: '11px',
              fontWeight: 700,
              color: (theme) => theme.palette.greys.medium,
              backgroundColor: (theme) => theme.palette.backgrounds.main_new,
              borderRadius: '6px',
              px: '6px',
              py: '4px',
            }}
          >
            {languageBadge}
          </Box>
        )}
        {adminActions && (
          <Box sx={{ flexShrink: 0, display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
            {adminActions}
          </Box>
        )}
      </Stack>

      {(isMostPopular || isBestConversion) && (
        <Stack direction="row" gap="6px" flexWrap="wrap">
          {isMostPopular && (
            <Box
              component="span"
              sx={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '.04em',
                textTransform: 'uppercase',
                borderRadius: '6px',
                px: '8px',
                py: '4px',
                backgroundColor: (theme) => theme.palette.backgrounds.lightOrange,
                color: '#8A3F01',
              }}
            >
              {t('useCaseStorePopular')}
            </Box>
          )}
          {isBestConversion && (
            <Box
              component="span"
              sx={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '.04em',
                textTransform: 'uppercase',
                borderRadius: '6px',
                px: '8px',
                py: '4px',
                backgroundColor: (theme) => theme.palette.backgrounds.lightGreen,
                color: '#2C5A00',
              }}
            >
              {t('useCaseStoreTopConversion')}
            </Box>
          )}
        </Stack>
      )}

      <Typography
        sx={{
          fontSize: '14px',
          color: '#555',
          lineHeight: 1.5,
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          // Without this the clamp cut mid-word with no visual cue, so descriptions read
          // as if they had simply been saved broken ("…upon acceptance, a").
          textOverflow: 'ellipsis',
        }}
      >
        {funnel.description}
      </Typography>

      {/* The metrics row replaces the old description-only body: conversion, uses and step
          count are what actually distinguish two templates from one another. */}
      <Stack direction="row" gap="12px" sx={{ py: '12px', borderTop: '1px solid #F0F0F5', borderBottom: '1px solid #F0F0F5' }}>
        {metrics.map((metric) => (
          <Stack key={metric.label} sx={{ flex: 1, gap: '2px' }}>
            <Typography sx={{ fontSize: '12px', color: (theme) => theme.palette.greys.medium }}>
              {metric.label}
            </Typography>
            <Typography sx={{ fontSize: '17px', fontWeight: 500 }}>{metric.value}</Typography>
          </Stack>
        ))}
      </Stack>

      {/* Pushed to the bottom so footers line up across a row of unevenly long descriptions. */}
      <Stack direction="row" alignItems="center" gap="12px" sx={{ mt: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <Tooltip title={funnel.liked ? t('useCaseStoreUnlike') : t('useCaseStoreLike')}>
          <Box
            component="span"
            sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: (theme) => theme.palette.greys.medium }}
          >
            <IconButton size="small" sx={{ p: 0 }} onClick={() => onLike(funnel._id)} disabled={likeLoading}>
              <StarIcon
                sx={{ fontSize: '18px' }}
                color={likeLoading ? 'disabled' : funnel.liked || likes > 30 ? 'warning' : 'action'}
              />
            </IconButton>
            {likes}
          </Box>
        </Tooltip>
        {funnel.creatorName && (
          <Typography sx={{ fontSize: '13px', color: (theme) => theme.palette.greys.medium, overflowWrap: 'anywhere' }}>
            {funnel.creatorName}
          </Typography>
        )}
        <Box sx={{ ml: 'auto' }}>
          <PaiButton
            text={t('useCaseStoreApply')}
            size="small"
            border="round"
            action={() => onApply(funnel)}
            style={{ height: 36, padding: '0 18px', fontSize: 14, fontWeight: 700, borderRadius: 18 }}
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default GlobalFunnelCard;
